/*
 * trial-pdp.js: helpers for the trial product templates.
 * - Submit guard (W35): an add pressed while a variant change is loading waits, then re-submits, so Horizon's
 *   property-less queued add never runs and properties[Name] survives. Also trims the name before FormData.
 * - trial-name-field-component (Template B), trial-sticky-bar, trial-card-component (collector card).
 * - A short vibration on a successful add (Android), and a quiet fallback for Files images still uploading.
 */
import { Component } from '@theme/component';

const warn = (error) => {
  if (error?.name !== 'AbortError') console.warn('[trial-pdp]', error);
};
const sectionOf = (element) => element.closest('.shopify-section') ?? document.body;
const clean = (value, max) =>
  value
    .replace(/[^A-Za-z0-9 ]/g, '')
    .replace(/ {2,}/g, ' ')
    .slice(0, max);

/* ---------- Submit guard ---------- */

const guarded = new WeakSet();

function guardSection(section) {
  if (guarded.has(section)) return;
  guarded.add(section);
  let pending = null;

  section.addEventListener('shopify:product:select', (event) => {
    const current = (pending = Promise.resolve(/** @type {any} */ (event).promise).catch(() => {}));
    current.finally(() => {
      if (pending === current) pending = null;
    });
  });

  // Window capture runs before Horizon's document-level on:submit delegation.
  window.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form' || !section.contains(form)) return;
      for (const field of section.querySelectorAll('input[name^="properties["]')) {
        if (field instanceof HTMLInputElement && !field.disabled) field.value = field.value.trim().replace(/\s+/g, ' ');
      }
      if (!pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      await pending;
      await new Promise((resolve) => setTimeout(resolve, 0));
      const live = document.getElementById(form.id);
      if (!(live instanceof HTMLFormElement)) return;
      const button = submitter instanceof HTMLButtonElement && submitter.isConnected && submitter.form === live ? submitter : undefined;
      live.requestSubmit(button);
    },
    { capture: true }
  );
}

function watchImage(image) {
  if (!(image instanceof HTMLImageElement) || image.dataset.trialWatched) return;
  image.dataset.trialWatched = '';
  const fail = () => {
    image.hidden = true;
  };
  if (image.complete && image.naturalWidth === 0 && image.currentSrc) fail();
  else image.addEventListener('error', fail, { once: true });
}

function init(root) {
  root.querySelectorAll('.trial-pdp').forEach((element) => guardSection(sectionOf(element)));
  root.querySelectorAll('img[data-trial-fallback]').forEach(watchImage);
}

init(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});

/* ---------- Add feedback: Horizon's hop and drawer run from theme settings; we add a short buzz ---------- */

document.addEventListener('shopify:cart:lines-update', (event) => {
  const detail = /** @type {any} */ (event);
  const target = event.target;
  if (target instanceof Element && !sectionOf(target).querySelector('.trial-pdp')) return;
  if (detail.action && detail.action !== 'add') return;
  detail.promise
    ?.then((result) => {
      if (!result?.detail?.didError) navigator.vibrate?.(30);
    })
    .catch(warn);
});

/* ---------- Name on the tag (Template B) ---------- */

class TrialNameFieldComponent extends Component {
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const input = this.#input;
    if (!input) return;

    sectionOf(this).addEventListener('shopify:product:select', this.#onSelect, { signal });
    input.addEventListener('input', this.#onInput, { signal });
    input.addEventListener('compositionend', this.#onInput, { signal });
    input.addEventListener('invalid', this.#onInvalid, { signal });
    input.addEventListener(
      'blur',
      () => {
        input.value = input.value.trim().replace(/\s+/g, ' ');
        this.#emit();
      },
      { signal }
    );

    // Share link restore: ?name= fills the field when the linked variant has a name.
    const shared = new URLSearchParams(location.search).get('name');
    if (shared && !input.disabled && !input.value) input.value = clean(shared, this.#max).trim();
    this.#emit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  get #input() {
    const input = this.refs.input;
    return input instanceof HTMLInputElement ? input : null;
  }

  get #max() {
    const max = this.#input?.maxLength ?? 0;
    return max > 0 ? max : 10;
  }

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const value = event.selectedOptions?.find?.((option) => option.name === this.dataset.nameOption)?.value;
    if (value !== undefined) this.#setActive(value !== this.dataset.firstValue);
    event.promise?.catch?.(warn);
  };

  #setActive(active) {
    const input = this.#input;
    if (!input) return;
    const { wrapper } = this.refs;
    if (wrapper instanceof HTMLElement) wrapper.hidden = !active;
    input.disabled = !active;
    input.required = active;
    if (!active) this.#clearError();
    this.#emit();
  }

  #onInput = (event) => {
    if (event.isComposing) return;
    const input = this.#input;
    if (!input) return;
    const next = clean(input.value, this.#max);
    if (next !== input.value) {
      input.value = next;
      this.#say(this.dataset.filtered);
    }
    if (input.validity.valid) this.#clearError();
    this.#emit();
  };

  #onInvalid = (event) => {
    event.preventDefault();
    const input = this.#input;
    const { error } = this.refs;
    if (error instanceof HTMLElement) error.hidden = false;
    input?.setAttribute('aria-invalid', 'true');
    if (input && document.activeElement !== input) input.focus();
  };

  #clearError() {
    const { error } = this.refs;
    if (error instanceof HTMLElement) error.hidden = true;
    this.#input?.removeAttribute('aria-invalid');
  }

  #emit() {
    const input = this.#input;
    if (!input) return;
    const { counter } = this.refs;
    if (counter) counter.textContent = `${input.value.length}/${this.#max}`;
    this.dispatchEvent(
      new CustomEvent('trial:name-change', {
        bubbles: true,
        detail: { name: input.disabled ? '' : input.value.trim() },
      })
    );
  }

  #say(text) {
    const { status } = this.refs;
    if (!(status instanceof HTMLElement) || !text) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = text;
    });
  }

  async share() {
    const url = new URL(location.href);
    const form = document.getElementById(this.dataset.formId || '');
    const id = form?.querySelector('input[name="id"]');
    if (id instanceof HTMLInputElement && id.value) url.searchParams.set('variant', id.value);
    // Template B renders the share button as its own block after Add to cart, so read the section's name field.
    const input = this.#input ?? sectionOf(this).querySelector('[data-trial-name-input]');
    const name = input instanceof HTMLInputElement && !input.disabled ? input.value.trim() : '';
    if (name) url.searchParams.set('name', name);
    else url.searchParams.delete('name');
    try {
      await navigator.clipboard.writeText(url.href);
      this.#say(this.dataset.copied);
    } catch {
      const { copyWrap, copyInput } = this.refs;
      if (copyWrap instanceof HTMLElement) copyWrap.hidden = false;
      if (copyInput instanceof HTMLInputElement) {
        copyInput.value = url.href;
        copyInput.focus();
        copyInput.select();
      }
    }
  }
}

/* ---------- Sticky add to cart (phones) ---------- */

class TrialStickyBar extends HTMLElement {
  #observer = null;
  #controller = null;
  #button = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = sectionOf(this);
    const bar = this.querySelector('.trial-sticky__bar');
    const submit = this.querySelector('.trial-sticky__button');
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    let above = false;
    let footerInView = false;

    const update = () => {
      const stuck = above && !footerInView;
      this.dataset.stuck = String(stuck);
      if (bar instanceof HTMLElement) bar.inert = !stuck;
      document.documentElement.classList.toggle('trial-sticky-on', stuck);
    };
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === footer) footerInView = entry.isIntersecting;
        else above = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      }
      update();
    });
    const watch = () => {
      const button = section.querySelector('product-form-component [ref="addToCartButton"]');
      if (button === this.#button) return;
      if (this.#button) this.#observer?.unobserve(this.#button);
      this.#button = button;
      if (button) this.#observer?.observe(button);
    };
    watch();
    if (footer) this.#observer.observe(footer);

    section.addEventListener(
      'shopify:product:select',
      (event) => {
        /** @type {any} */ (event).promise
          ?.then((result) => {
            requestAnimationFrame(watch);
            if (submit instanceof HTMLButtonElement) submit.disabled = !result?.detail?.resource?.available;
          })
          .catch(warn);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
    this.#button = null;
    document.documentElement.classList.remove('trial-sticky-on');
  }
}

/* ---------- Collector card ---------- */

class TrialCardComponent extends Component {
  /**
   * Bound to `on:click` on both the toggle button and the card art. A real button means the browser
   * turns Enter and Space into the same click, so all three routes run this once.
   */
  flip() {
    const flipped = this.dataset.flipped !== 'true';
    this.dataset.flipped = String(flipped);
    const { front, back, toggle, status } = this.refs;
    if (front instanceof HTMLElement) front.inert = flipped;
    if (back instanceof HTMLElement) back.inert = !flipped;
    if (toggle instanceof HTMLElement) {
      toggle.textContent = (flipped ? this.dataset.frontLabel : this.dataset.backLabel) || '';
      toggle.setAttribute('aria-expanded', String(flipped));
    }
    if (status instanceof HTMLElement) {
      const said = (flipped ? this.dataset.backSaid : this.dataset.frontSaid) || '';
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = said;
      });
    }
  }
}

if (!customElements.get('trial-name-field-component')) {
  customElements.define('trial-name-field-component', TrialNameFieldComponent);
}
if (!customElements.get('trial-sticky-bar')) customElements.define('trial-sticky-bar', TrialStickyBar);
if (!customElements.get('trial-card-component')) customElements.define('trial-card-component', TrialCardComponent);
